import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { Table, Container, Button, Modal, Form } from "react-bootstrap";
import Contexts from "../../context/Contexts"; // ajustá según tu estructura

const apiUrl = process.env.REACT_APP_API_URL;

export default function FacturacionTesoreriaList() {
  const dataContext = useContext(Contexts.DataContext);

  const {
    formasPagoTesoreria,
    librosIvaTabla,
    ptosVentaTabla,
    tiposComprobanteTabla,
    imputacionContableTabla,
    empresaSeleccionada,
  } = dataContext;

  // =========================
  // CLIENTES: AUTOCOMPLETE + CACHE (sin traer todos)
  // =========================
  const [clientesCache, setClientesCache] = useState({}); // { [id]: clienteObj }
  const [comprobantes, setComprobantes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedComprobante, setSelectedComprobante] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [empresas, setEmpresas] = useState([]);

  // =========================
  // FILTROS Y ORDENAMIENTO
  // =========================
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
  const [filtroTipoComprobante, setFiltroTipoComprobante] = useState("");

  const [ordenColumna, setOrdenColumna] = useState("fechacomprobante");
  const [ordenDireccion, setOrdenDireccion] = useState("desc");
  const hoy = new Date().toISOString().split("T")[0];

  const getClienteCachedLabel = (id) => {
    const c = clientesCache[Number(id)];
    return c ? clienteLabel(c) : `ID ${id}`;
  };

  const fetchClienteById = useCallback(
    async (id) => {
      const key = Number(id);
      if (!key || clientesCache[key]) return;

      try {
        const res = await fetch(`${apiUrl}/clientespersonatabla/id/${key}`, {
          credentials: "include",
        });
        if (!res.ok) return;

        const data = await res.json();
        setClientesCache((prev) => ({ ...prev, [key]: data }));
      } catch (e) {
        // silencioso
      }
    },
    [apiUrl, clientesCache]
  );

  const buscarClientesPersona = useCallback(
    async (q) => {
      const query = String(q || "").trim();
      if (query.length < 2) return [];

      const res = await fetch(
        `${apiUrl}/clientespersonatabla/buscar?q=${encodeURIComponent(query)}&limit=20`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("No se pudo buscar clientes");
      const data = await res.json();
      return Array.isArray(data?.rows) ? data.rows : [];
    },
    [apiUrl]
  );

  // ====== Estado AUTOCOMPLETE CREATE ======
  const [clienteQueryCreate, setClienteQueryCreate] = useState("");
  const [clienteResultadosCreate, setClienteResultadosCreate] = useState([]);
  const [clienteLoadingCreate, setClienteLoadingCreate] = useState(false);
  const [clienteErrorCreate, setClienteErrorCreate] = useState("");

  // ====== Estado AUTOCOMPLETE EDIT ======
  const [clienteQueryEdit, setClienteQueryEdit] = useState("");
  const [clienteResultadosEdit, setClienteResultadosEdit] = useState([]);
  const [clienteLoadingEdit, setClienteLoadingEdit] = useState(false);
  const [clienteErrorEdit, setClienteErrorEdit] = useState("");


  const getNuevoComprobanteInicial = () => ({
    nrocomprobante: "",
    iva105: 0,
    iva21: 0,
    neto: 0,
    total: 0,
    tipocomprobante_id: "",
    ptoventa_id: "",
    libroiva_id: "",
    cliente_id: "",
    fechacomprobante: hoy,
    fechavencimiento: hoy,
    imputacioncontable_id: "",
    observaciones: "",
    ctactecliente_id: "",
    formapago_id: "",
    estadoPago: "pendiente",
    conFactura: true,
    ajusta_cierre_z: false,
  });



  const [nuevoComprobante, setNuevoComprobante] = useState(getNuevoComprobanteInicial);



  const fmtMoney = (n) =>
    (Number(n) || 0).toLocaleString("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const toNumber2 = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
  };

  const calcIVA105 = () => {
    const neto = Number(nuevoComprobante.neto || 0);
    setNuevoComprobante((p) => ({ ...p, iva105: toNumber2(neto * 0.105) }));
  };

  const calcIVA21 = () => {
    const neto = Number(nuevoComprobante.neto || 0);
    setNuevoComprobante((p) => ({ ...p, iva21: toNumber2(neto * 0.21) }));
  };

  // =========================
  // CALC IVA - EDICIÓN
  // =========================
  const calcIVA105Edit = () => {
    const neto = Number(selectedComprobante?.neto || 0);
    setSelectedComprobante((p) => ({ ...p, iva105: toNumber2(neto * 0.105) }));
  };

  const calcIVA21Edit = () => {
    const neto = Number(selectedComprobante?.neto || 0);
    setSelectedComprobante((p) => ({ ...p, iva21: toNumber2(neto * 0.21) }));
  };

  const clienteLabel = (c) => {
    if (!c) return "";

    // si el endpoint devuelve algo anidado, lo contemplamos
    const persona = c?.ClientePersonatabla || c?.clientePersona || c;
    const cliBase = c?.ClienteTabla || c?.cliente || c;

    const apellido = persona?.apellido || "";
    const nombre = persona?.nombre || "";
    const doc = persona?.numero || persona?.cuil || "";
    const razonSocial = cliBase?.razonsocial || cliBase?.nombre || cliBase?.descripcion || "";

    // 1) Persona: "APELLIDO, Nombre (DNI/CUIL)"
    const personaTxt =
      (apellido || nombre)
        ? `${apellido ? apellido + ", " : ""}${nombre}${doc ? ` (${doc})` : ""}`
        : "";

    // 2) Empresa/Razón social si existiera
    const baseTxt = razonSocial ? `${razonSocial}${doc ? ` (${doc})` : ""}` : "";

    // priorizamos personaTxt, si no existe usamos baseTxt, sino ID
    return (personaTxt || baseTxt || `ID ${c.id}`).trim();
  };

  const derivedTotal =
    Number(nuevoComprobante.neto || 0) +
    Number(nuevoComprobante.iva105 || 0) +
    Number(nuevoComprobante.iva21 || 0);

  // =========================
  // VALIDACIÓN MODAL CREACIÓN
  // =========================
  const canCrearComprobante = useMemo(() => {
    if (!empresaSeleccionada?.id) return false;

    const req = nuevoComprobante || {};

    // requeridos (los IVA y observaciones NO)
    const requiredText = [
      "nrocomprobante",
      "tipocomprobante_id",
      "ptoventa_id",
      "libroiva_id",
      "cliente_id",
      "fechacomprobante",
      "fechavencimiento",
      "imputacioncontable_id",
      "formapago_id",
      "estadoPago",
    ];

    for (const k of requiredText) {
      if (req[k] === null || req[k] === undefined || String(req[k]).trim() === "") return false;
    }

    // Neto válido
    if (
      req.neto === "" ||
      req.neto === null ||
      !Number.isFinite(Number(req.neto))
    ) {
      return false;
    }

    // IVA revisado después de modificar el neto.
    // Se permite 0 para operaciones sin IVA en esa alícuota.
    if (
      req.iva105 === null ||
      req.iva105 === undefined ||
      req.iva105 === "" ||
      req.iva21 === null ||
      req.iva21 === undefined ||
      req.iva21 === ""
    ) {
      return false;
    }

    if (
      !Number.isFinite(Number(req.iva105)) ||
      !Number.isFinite(Number(req.iva21))
    ) {
      return false;
    }

    return true;
  }, [nuevoComprobante, empresaSeleccionada]);

  // =========================
  // VALIDACIÓN MODAL EDICIÓN
  // =========================
  const canGuardarCambios = useMemo(() => {
    if (!empresaSeleccionada?.id) return false;
    if (!selectedComprobante?.id) return false;

    const req = selectedComprobante || {};

    // requeridos (los IVA y observaciones NO)
    const requiredText = [
      "nrocomprobante",
      "tipocomprobante_id",
      "ptoventa_id",
      "libroiva_id",
      "cliente_id",
      "fechacomprobante",
      "fechavencimiento",
      "imputacioncontable_id",
      "formapago_id",
      "estadoPago",
    ];

    for (const k of requiredText) {
      if (req[k] === null || req[k] === undefined || String(req[k]).trim() === "") return false;
    }

    // neto requerido (puede ser 0, pero debe ser número válido)
    const netoNum = Number(req.neto);
    if (!Number.isFinite(netoNum)) return false;

    return true;
  }, [selectedComprobante, empresaSeleccionada]);


  // Filtra libros IVA y puntos de venta por empresa seleccionada
  const librosIVAFiltrados = librosIvaTabla.filter(
    (l) => l.empresa_id === empresaSeleccionada?.id
  );

  const ptosVtaFiltrados = ptosVentaTabla.filter(
    (l) => l.empresa_id === empresaSeleccionada?.id
  );



  useEffect(() => {
    const fetchEmpresas = async () => {
      try {
        const res = await fetch(`${apiUrl}/empresas`, { credentials: "include" });
        if (!res.ok) throw new Error("No se pudieron cargar las empresas");
        const data = await res.json();
        setEmpresas(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching empresas:", error);
      }
    };
    fetchEmpresas();
  }, []);



  const empresasMap = useMemo(
    () => Object.fromEntries(empresas.map((e) => [e.id, e])),
    [empresas]
  );
  // =========================
  // ORDENAMIENTO DE COLUMNAS
  // =========================
  const cambiarOrden = (columna) => {
    if (ordenColumna === columna) {
      setOrdenDireccion((prev) => prev === "asc" ? "desc" : "asc");
    } else {
      setOrdenColumna(columna);
      setOrdenDireccion("asc");
    }
  };

  const indicadorOrden = (columna) => {
    if (ordenColumna !== columna) return " ↕";
    return ordenDireccion === "asc" ? " ↑" : " ↓";
  };

  // =========================
  // COMPROBANTES FILTRADOS
  // =========================
  const comprobantesFiltrados = useMemo(() => {
    return comprobantes.filter((comp) => {
      const fecha = String(comp.fechacomprobante || "").slice(0, 10);

      if (filtroFechaDesde && fecha < filtroFechaDesde) {
        return false;
      }

      if (filtroFechaHasta && fecha > filtroFechaHasta) {
        return false;
      }

      if (
        filtroTipoComprobante &&
        Number(comp.tipocomprobante_id) !== Number(filtroTipoComprobante)
      ) {
        return false;
      }

      return true;
    });
  }, [
    comprobantes,
    filtroFechaDesde,
    filtroFechaHasta,
    filtroTipoComprobante,
  ]);

  // =========================
  // COMPROBANTES ORDENADOS
  // =========================
  const comprobantesOrdenados = useMemo(() => {
    const registros = [...comprobantesFiltrados];

    const obtenerValor = (comp) => {
      if (ordenColumna === "tipo") {
        const tipo = tiposComprobanteTabla.find(
          (t) => Number(t.id) === Number(comp.tipocomprobante_id)
        );

        return tipo?.descripcion || "";
      }

      return comp[ordenColumna];
    };

    const columnasNumericas = [
      "id",
      "neto",
      "iva105",
      "iva21",
      "total",
    ];

    registros.sort((a, b) => {
      const valorA = obtenerValor(a);
      const valorB = obtenerValor(b);

      let resultado = 0;

      if (columnasNumericas.includes(ordenColumna)) {
        resultado = Number(valorA || 0) - Number(valorB || 0);
      } else {
        resultado = String(valorA ?? "").localeCompare(
          String(valorB ?? ""),
          "es",
          { numeric: true, sensitivity: "base" }
        );
      }

      // Desempate para mantener un orden consistente
      if (resultado === 0) {
        resultado = Number(a.id) - Number(b.id);
      }

      return ordenDireccion === "asc" ? resultado : -resultado;
    });

    return registros;
  }, [
    comprobantesFiltrados,
    ordenColumna,
    ordenDireccion,
    tiposComprobanteTabla,
  ]);

  // =========================
  // TOTALES DE LOS FILTRADOS
  // =========================
  const totalesFiltrados = useMemo(() => {
    return comprobantesFiltrados.reduce(
      (acc, comp) => {
        const tipo = tiposComprobanteTabla.find(
          (t) => Number(t.id) === Number(comp.tipocomprobante_id)
        );

        const descripcion = String(tipo?.descripcion || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toUpperCase();

        const esNotaCredito = /NOTA\s+(DE\s+)?CREDITO/.test(descripcion);
        const signo = esNotaCredito ? -1 : 1;

        acc.neto += signo * Math.abs(Number(comp.neto || 0));
        acc.iva105 += signo * Math.abs(Number(comp.iva105 || 0));
        acc.iva21 += signo * Math.abs(Number(comp.iva21 || 0));
        acc.total += signo * Math.abs(Number(comp.total || 0));

        return acc;
      },
      { neto: 0, iva105: 0, iva21: 0, total: 0 }
    );
  }, [comprobantesFiltrados, tiposComprobanteTabla]);
  useEffect(() => {
    // precargar clientes de los comprobantes visibles
    const ids = Array.from(
      new Set((comprobantes || []).map((c) => Number(c.cliente_id)).filter(Boolean))
    );

    ids.forEach((id) => {
      if (!clientesCache[id]) fetchClienteById(id);
    });
  }, [comprobantes, clientesCache, fetchClienteById]);

  // Cargar comprobantes (con filtro empresa si corresponde)
  const loadComprobantes = useCallback(async () => {
    try {
      let url = `${apiUrl}/comprobantes-ingreso`;

      if (dataContext.empresaSeleccionada) {
        url += `?empresa_id=${dataContext.empresaSeleccionada.id}`;
      }

      const res = await fetch(url, {
        credentials: "include",
      });

      const data = await res.json();
      setComprobantes(data.sort((a, b) => a.id - b.id));
    } catch (error) {
      console.error("❌ Error al cargar comprobantes:", error);
    }
  }, [dataContext.empresaSeleccionada]);


  // ===== Debounce CREATE =====
  useEffect(() => {
    if (!showCreateModal) return;

    let cancel = false;
    const t = setTimeout(async () => {
      if (cancel) return;

      const q = clienteQueryCreate.trim();
      if (q.length < 2) {
        setClienteResultadosCreate([]);
        setClienteErrorCreate("");
        return;
      }

      setClienteLoadingCreate(true);
      setClienteErrorCreate("");

      try {
        const rows = await buscarClientesPersona(q);
        setClienteResultadosCreate(rows);
      } catch (e) {
        setClienteErrorCreate(e.message || "Error buscando clientes");
        setClienteResultadosCreate([]);
      } finally {
        setClienteLoadingCreate(false);
      }
    }, 300);

    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [clienteQueryCreate, showCreateModal, buscarClientesPersona]);

  // ===== Debounce EDIT =====
  useEffect(() => {
    if (!showModal) return;

    let cancel = false;
    const t = setTimeout(async () => {
      if (cancel) return;

      const q = clienteQueryEdit.trim();
      if (q.length < 2) {
        setClienteResultadosEdit([]);
        setClienteErrorEdit("");
        return;
      }

      setClienteLoadingEdit(true);
      setClienteErrorEdit("");

      try {
        const rows = await buscarClientesPersona(q);
        setClienteResultadosEdit(rows);
      } catch (e) {
        setClienteErrorEdit(e.message || "Error buscando clientes");
        setClienteResultadosEdit([]);
      } finally {
        setClienteLoadingEdit(false);
      }
    }, 300);

    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [clienteQueryEdit, showModal, buscarClientesPersona]);
  // ... dentro del componente FacturacionTesoreriaList

  const handleEliminarComprobante = async (id) => {
    if (!window.confirm("¿Está seguro que desea eliminar este comprobante?")) return;

    try {
      const res = await fetch(`${apiUrl}/comprobantes-ingreso/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || "No se pudo eliminar el comprobante");
      }

      await loadComprobantes();
    } catch (error) {
      console.error("❌ Error al eliminar comprobante:", error);
      alert(error.message);
    }
  };


  // Handlers de formulario de creación de comprobante
  const handleNuevoChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numeric = new Set(["neto", "iva105", "iva21"]);
    const v = type === "checkbox" ? checked : value;

    setNuevoComprobante((prev) => {
      const next = {
        ...prev,
        [name]: numeric.has(name) ? (v === "" ? "" : Number(v)) : v,
      };

      // Si cambia el tipo de comprobante, reiniciar el ajuste de Cierre Z
      if (name === "tipocomprobante_id") {
        next.ajusta_cierre_z = false;
      }

      // ✅ si cambia neto: reset IVA
      if (name === "neto") {
        next.iva105 = 0;
        next.iva21 = 0;
      }

      return next;
    });
  };

  useEffect(() => {
    loadComprobantes();
  }, [loadComprobantes]);

  // Abrir modal de edición
  const handleDoubleClick = async (comprobante) => {
    if (!empresaSeleccionada?.id) {
      alert("Debe seleccionar una empresa para editar comprobantes.");
      return;
    }

    try {
      const res = await fetch(
        `${apiUrl}/comprobantes-ingreso/${comprobante.id}`,
        {
          credentials: "include",
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error || "No se pudo obtener el comprobante."
        );
      }

      // El endpoint devuelve el comprobante individual.
      const comp = data.comprobante || data;

      if (Number(comp.empresa_id) !== Number(empresaSeleccionada.id)) {
        throw new Error(
          "El comprobante no pertenece a la empresa seleccionada."
        );
      }

      // Normalizar IVA nulo de registros anteriores a cero.
      // El IVA se dejará en null solo si se modifica el neto.
      setSelectedComprobante({
        ...comp,
        iva105: comp.iva105 ?? 0,
        iva21: comp.iva21 ?? 0,
        ajusta_cierre_z: !!comp.ajusta_cierre_z,
      });

      setClienteQueryEdit("");
      setClienteResultadosEdit([]);
      setClienteErrorEdit("");

      setShowModal(true);

    } catch (error) {
      console.error("Error al abrir comprobante:", error);
      alert(error.message || "No se pudo abrir el comprobante.");
    }
  };

  const handleCloseModal = () => {
    setSelectedComprobante(null);
    setShowModal(false);

    setClienteQueryEdit("");
    setClienteResultadosEdit([]);
    setClienteErrorEdit("");
    setClienteLoadingEdit(false);

  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numeric = new Set(["neto", "iva105", "iva21", "total"]);
    const v = type === "checkbox" ? checked : value;

    setSelectedComprobante((prev) => {
      const next = {
        ...prev,
        [name]: numeric.has(name) ? (v === "" ? "" : Number(v)) : v,
      };

      // Si cambia el neto, invalidar los IVA anteriores
      if (name === "neto" && String(v) !== String(prev.neto ?? "")) {
        next.iva105 = null;
        next.iva21 = null;
      }

      // Si cambia el tipo de comprobante,
      // evitar conservar un ajuste de Z de una nota de crédito anterior
      if (name === "tipocomprobante_id") {
        next.ajusta_cierre_z = false;
      }

      return next;
    });
  };


  const handleGuardarCambios = async () => {
    if (!canGuardarCambios) {
      alert("Revisá los campos obligatorios y los importes de IVA.");
      return;
    }

    try {
      const payload = {
        ...selectedComprobante,

        neto: toNumber2(selectedComprobante.neto),
        iva105: toNumber2(selectedComprobante.iva105),
        iva21: toNumber2(selectedComprobante.iva21),

        total: toNumber2(derivedTotalEdit),

        // Solo las notas de crédito pueden corregir Cierres Z
        ajusta_cierre_z:
          tiposComprobanteTabla.some(
            (t) =>
              Number(t.id) ===
              Number(selectedComprobante.tipocomprobante_id) &&
              /NOTA\s+(DE\s+)?CREDITO/i.test(
                String(t.descripcion || "")
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
              )
          ) && !!selectedComprobante.ajusta_cierre_z,
      };

      const res = await fetch(
        `${apiUrl}/comprobantes-ingreso/${selectedComprobante.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));

        throw new Error(
          errorData?.error || "No se pudo actualizar el comprobante."
        );
      }

      await loadComprobantes();

      handleCloseModal();

    } catch (error) {
      console.error("Error al actualizar comprobante:", error);
      alert(error.message);
    }
  };


  // Crear Comprobante y, si forma de pago es Tarjeta, abrir Modal Tarjeta
  const handleCrearComprobante = async () => {
    try {
      const comprobanteConEmpresa = {
        ...nuevoComprobante,
        total: toNumber2(derivedTotal),
        empresa_id: dataContext.empresaSeleccionada
          ? dataContext.empresaSeleccionada.id
          : null,
      };
      const res = await fetch(`${apiUrl}/comprobantes-ingreso`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(comprobanteConEmpresa),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || "No se pudo crear el comprobante");
      }

      const creado = await res.json();
      await loadComprobantes();
      setShowCreateModal(false);
      // setNuevoComprobante({
      //   nrocomprobante: "",
      //   iva105: 0,
      //   iva21: 0,
      //   neto: 0,
      //   total: 0,
      //   tipocomprobante_id: "",
      //   ptoventa_id: "",
      //   libroiva_id: "",
      //   cliente_id: "",
      //   fechacomprobante: "",
      //   fechavencimiento: "",
      //   imputacioncontable_id: "",
      //   observaciones: "",
      //   ctactecliente_id: "",
      //   formapago_id: "",
      //   estadoPago: "pendiente",
      //   conFactura: true,
      //   ajusta_cierre_z: !!nuevoComprobante.ajusta_cierre_z,
      // });
      // Restablecer todos los campos a sus valores iniciales
      setNuevoComprobante(getNuevoComprobanteInicial());
      setClienteQueryCreate("");
      setClienteResultadosCreate([]);
      setClienteErrorCreate("");
      setClienteLoadingCreate(false);

    } catch (error) {
      console.error("❌ Error al crear comprobante:", error);
      alert(error.message);
    }
  };

  const derivedTotalEdit =
    Number(selectedComprobante?.neto || 0) +
    Number(selectedComprobante?.iva105 || 0) +
    Number(selectedComprobante?.iva21 || 0);


  return (
    <Container>
      <h1 className="my-list-title dark-text">Facturación</h1>

      <div className="mb-3">
        <Button
          className="mx-3"
          variant="success"
          disabled={!empresaSeleccionada}
          onClick={() => {
            if (!empresaSeleccionada) {
              alert("Debe seleccionar una empresa para emitir comprobantes.");
              return;
            }
            setNuevoComprobante(getNuevoComprobanteInicial()); // ← reset

            setClienteQueryCreate("");
            setClienteResultadosCreate([]);
            setClienteErrorCreate("");
            setClienteLoadingCreate(false);

            setShowCreateModal(true);
          }}

        >
          Nuevo Comprobante
        </Button>


      </div>

      {/* =========================
    FILTROS DE COMPROBANTES
========================= */}
      <div className="border rounded p-3 mb-3">
        <div className="row g-3">

          <div className="col-md-3">
            <Form.Label>Fecha desde</Form.Label>
            <Form.Control
              type="date"
              value={filtroFechaDesde}
              onChange={(e) => setFiltroFechaDesde(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <Form.Label>Fecha hasta</Form.Label>
            <Form.Control
              type="date"
              value={filtroFechaHasta}
              onChange={(e) => setFiltroFechaHasta(e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <Form.Label>Tipo de comprobante</Form.Label>
            <Form.Select
              value={filtroTipoComprobante}
              onChange={(e) => setFiltroTipoComprobante(e.target.value)}
            >
              <option value="">Todos los comprobantes</option>

              {tiposComprobanteTabla.map((tipo) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.descripcion}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="col-md-2 d-flex align-items-end">
            <Button
              variant="outline-secondary"
              className="w-100"
              onClick={() => {
                setFiltroFechaDesde("");
                setFiltroFechaHasta("");
                setFiltroTipoComprobante("");
              }}
            >
              Limpiar filtros
            </Button>
          </div>

        </div>

        <div className="text-muted small mt-3">
          Mostrando <strong>{comprobantesFiltrados.length}</strong> de{" "}
          <strong>{comprobantes.length}</strong> comprobantes.
        </div>
      </div>

      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="align-middle">

          <thead>
            <tr>
              <th
                onClick={() => cambiarOrden("id")}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                #{indicadorOrden("id")}
              </th>

              <th
                onClick={() => cambiarOrden("fechacomprobante")}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Fecha{indicadorOrden("fechacomprobante")}
              </th>

              <th
                onClick={() => cambiarOrden("tipo")}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Tipo comprobante{indicadorOrden("tipo")}
              </th>

              <th
                onClick={() => cambiarOrden("nrocomprobante")}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Comprobante{indicadorOrden("nrocomprobante")}
              </th>

              <th
                className="text-end"
                onClick={() => cambiarOrden("neto")}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Neto{indicadorOrden("neto")}
              </th>

              <th
                className="text-end"
                onClick={() => cambiarOrden("iva105")}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                IVA 10,5%{indicadorOrden("iva105")}
              </th>

              <th
                className="text-end"
                onClick={() => cambiarOrden("iva21")}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                IVA 21%{indicadorOrden("iva21")}
              </th>

              <th
                className="text-end"
                onClick={() => cambiarOrden("total")}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Total{indicadorOrden("total")}
              </th>

              <th>Cliente</th>
              <th>Forma de pago</th>
              <th>Empresa</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {comprobantesOrdenados.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center text-muted py-4">
                  No se encontraron comprobantes con los filtros seleccionados.
                </td>
              </tr>
            ) : (
              comprobantesOrdenados.map((comp) => {
                const tipo = tiposComprobanteTabla.find(
                  (t) => Number(t.id) === Number(comp.tipocomprobante_id)
                );

                const esNotaCredito = /NOTA\s+(DE\s+)?CREDITO/i.test(
                  String(tipo?.descripcion || "")
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                );

                return (
                  <tr
                    key={comp.id}
                    onDoubleClick={() => handleDoubleClick(comp)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{comp.id}</td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      {comp.fechacomprobante
                        ? String(comp.fechacomprobante)
                          .slice(0, 10)
                          .split("-")
                          .reverse()
                          .join("/")
                        : "-"}
                    </td>

                    <td>
                      <span
                        className={
                          esNotaCredito
                            ? "badge bg-warning text-dark"
                            : "badge bg-secondary"
                        }
                      >
                        {tipo?.descripcion || `ID ${comp.tipocomprobante_id}`}
                      </span>
                    </td>

                    <td>{comp.nrocomprobante}</td>

                    <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                      {fmtMoney(comp.neto)}
                    </td>

                    <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                      {fmtMoney(comp.iva105)}
                    </td>

                    <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                      {fmtMoney(comp.iva21)}
                    </td>

                    <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                      <strong>{fmtMoney(comp.total)}</strong>
                    </td>

                    <td>
                      {comp.cliente_id
                        ? clientesCache[Number(comp.cliente_id)]
                          ? clienteLabel(clientesCache[Number(comp.cliente_id)])
                          : "Cargando..."
                        : "-"}
                    </td>

                    <td>
                      {formasPagoTesoreria.find(
                        (fp) => Number(fp.id) === Number(comp.formapago_id)
                      )?.descripcion || comp.formapago_id}
                    </td>

                    <td>
                      {empresasMap[comp.empresa_id]?.nombrecorto ||
                        empresasMap[comp.empresa_id]?.nombre ||
                        `ID ${comp.empresa_id}`}
                    </td>

                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEliminarComprobante(comp.id);
                        }}
                      >
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>

          <tfoot>
            <tr className="table-secondary fw-bold">
              <td colSpan={4} className="text-end">
                TOTALES ({comprobantesFiltrados.length} comprobantes)
              </td>

              <td className="text-end">
                {fmtMoney(totalesFiltrados.neto)}
              </td>

              <td className="text-end">
                {fmtMoney(totalesFiltrados.iva105)}
              </td>

              <td className="text-end">
                {fmtMoney(totalesFiltrados.iva21)}
              </td>

              <td className="text-end">
                {fmtMoney(totalesFiltrados.total)}
              </td>

              <td colSpan={4}></td>
            </tr>
          </tfoot>

        </Table>
      </div>

      {/* ===== Modal de edición ===== */}
      <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Comprobante</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedComprobante && (
            <Form>
              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>N° Comprobante</Form.Label>
                  <Form.Control
                    name="nrocomprobante"
                    value={selectedComprobante.nrocomprobante || ""}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Cliente</Form.Label>

                  <Form.Control
                    className="form-control my-input"
                    placeholder="Buscar por apellido, nombre, DNI/CUIL..."
                    value={clienteQueryEdit}
                    onChange={(e) => setClienteQueryEdit(e.target.value)}
                  />

                  {selectedComprobante?.cliente_id ? (
                    <div className="mt-2 d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        Seleccionado: <strong>{getClienteCachedLabel(selectedComprobante.cliente_id)}</strong>
                      </small>

                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          setSelectedComprobante((p) => ({ ...p, cliente_id: "" }));
                          setClienteQueryEdit("");
                          setClienteResultadosEdit([]);
                        }}
                      >
                        Quitar
                      </Button>
                    </div>
                  ) : (
                    <small className="text-muted">Escribí al menos 2 caracteres para buscar.</small>
                  )}

                  {(clienteLoadingEdit || clienteErrorEdit || clienteResultadosEdit.length > 0) && (
                    <div className="list-group mt-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                      {clienteLoadingEdit && <div className="list-group-item">Buscando...</div>}

                      {!clienteLoadingEdit && clienteErrorEdit && (
                        <div className="list-group-item text-danger">{clienteErrorEdit}</div>
                      )}

                      {!clienteLoadingEdit &&
                        !clienteErrorEdit &&
                        clienteResultadosEdit.map((c) => (
                          <button
                            type="button"
                            key={c.id}
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setSelectedComprobante((p) => ({ ...p, cliente_id: c.id }));
                              setClientesCache((prev) => ({ ...prev, [Number(c.id)]: c }));
                              setClienteQueryEdit(clienteLabel(c));
                              setClienteResultadosEdit([]);
                            }}
                          >
                            {clienteLabel(c)}
                          </button>
                        ))}
                    </div>
                  )}
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group className="mb-3 col-md-4">
                  <Form.Label>Neto</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="neto"
                    value={selectedComprobante.neto || 0}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3 col-md-4">
                  <Form.Label>IVA 10.5%</Form.Label>

                  <div className="d-flex gap-2">
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="iva105"
                      value={selectedComprobante.iva105 ?? ""}
                      onChange={handleChange}
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={calcIVA105Edit}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Calc
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3 col-md-4">
                  <Form.Label>IVA 21%</Form.Label>

                  <div className="d-flex gap-2">
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="iva21"
                      value={selectedComprobante.iva21 ?? ""}
                      onChange={handleChange}
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={calcIVA21Edit}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Calc
                    </Button>
                  </div>
                </Form.Group>

              </div>

              <div className="row">
                {/* 🔁 TOTAL (auto) en modal de edicion */}
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Total (auto)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="total"
                    value={toNumber2(derivedTotalEdit).toFixed(2)}
                    readOnly
                  />
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Forma de Pago (solo informativo)</Form.Label>
                  <Form.Select
                    name="formapago_id"
                    value={selectedComprobante.formapago_id || ""}
                    onChange={handleChange}
                    className="form-control my-input"
                  >
                    <option value="">Seleccione...</option>
                    {formasPagoTesoreria.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.descripcion}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Tipo Comprobante</Form.Label>
                  <Form.Select
                    name="tipocomprobante_id"
                    value={selectedComprobante.tipocomprobante_id || ""}
                    onChange={handleChange}
                    className="form-control my-input"
                  >
                    <option value="">Seleccione...</option>
                    {tiposComprobanteTabla.map((tc) => (
                      <option key={tc.id} value={tc.id}>
                        {tc.descripcion}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Punto de Venta</Form.Label>
                  <Form.Select
                    name="ptoventa_id"
                    value={selectedComprobante.ptoventa_id || ""}
                    onChange={handleChange}
                    className="form-control my-input"
                  >
                    <option value="">Seleccione...</option>
                    {ptosVtaFiltrados.map((pv) => (
                      <option key={pv.id} value={pv.id}>
                        {pv.descripcion}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Libro IVA</Form.Label>
                  <Form.Select
                    name="libroiva_id"
                    value={selectedComprobante?.libroiva_id || ""}
                    onChange={handleChange}
                    className="form-control my-input"
                  >
                    <option value="">Seleccione...</option>
                    {librosIVAFiltrados.map((l) => (
                      <option key={l.id} value={l.id}>
                        {`${l.mes} / ${l.anio}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Imputación Contable</Form.Label>
                  <Form.Select
                    name="imputacioncontable_id"
                    value={selectedComprobante.imputacioncontable_id || ""}
                    onChange={handleChange}
                    className="form-control my-input"
                  >
                    <option value="">Seleccione...</option>
                    {imputacionContableTabla.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.descripcion}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Fecha Comprobante</Form.Label>
                  <Form.Control
                    type="date"
                    name="fechacomprobante"
                    value={selectedComprobante.fechacomprobante || ""}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Fecha Vencimiento</Form.Label>
                  <Form.Control
                    type="date"
                    name="fechavencimiento"
                    value={selectedComprobante.fechavencimiento || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  name="observaciones"
                  rows={2}
                  value={selectedComprobante.observaciones || ""}
                  onChange={handleChange}
                />
              </Form.Group>

              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Estado Pago</Form.Label>
                  <Form.Select
                    name="estadoPago"
                    value={selectedComprobante.estadoPago || "pendiente"}
                    onChange={handleChange}
                    className="form-control my-input"
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="parcial">Parcial</option>
                    <option value="pagado">Pagado</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3 col-md-6 d-flex align-items-end">
                  <Form.Check
                    label="Con Factura"
                    name="conFactura"
                    checked={!!selectedComprobante.conFactura}
                    onChange={(e) =>
                      setSelectedComprobante({
                        ...selectedComprobante,
                        conFactura: e.target.checked,
                      })
                    }
                  />
                </Form.Group>

                {tiposComprobanteTabla.some(
                  (t) =>
                    Number(t.id) === Number(selectedComprobante.tipocomprobante_id) &&
                    /NOTA\s+(DE\s+)?CREDITO/i.test(
                      String(t.descripcion || "")
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                    )
                ) && (
                    <Form.Group className="mb-3 col-md-6">
                      <Form.Check
                        type="switch"
                        label="Corrige un Cierre Z"
                        name="ajusta_cierre_z"
                        checked={!!selectedComprobante.ajusta_cierre_z}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  )}

              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleGuardarCambios}
            disabled={!canGuardarCambios}
          >
            Guardar Cambios
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== Modal de creación ===== */}
      <Modal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setNuevoComprobante(getNuevoComprobanteInicial()); // ← reset

          setClienteQueryCreate("");
          setClienteResultadosCreate([]);
          setClienteErrorCreate("");
          setClienteLoadingCreate(false);
        }}
        backdrop="static"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Nuevo Comprobante</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>N° Comprobante</Form.Label>
                <Form.Control
                  name="nrocomprobante"
                  value={nuevoComprobante.nrocomprobante}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Cliente</Form.Label>

                <Form.Control
                  className="form-control my-input"
                  placeholder="Buscar por apellido, nombre, DNI/CUIL..."
                  value={clienteQueryCreate}
                  onChange={(e) => setClienteQueryCreate(e.target.value)}
                />

                {nuevoComprobante.cliente_id ? (
                  <div className="mt-2 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Seleccionado: <strong>{getClienteCachedLabel(nuevoComprobante.cliente_id)}</strong>
                    </small>

                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setNuevoComprobante((p) => ({ ...p, cliente_id: "" }));
                        setClienteQueryCreate("");
                        setClienteResultadosCreate([]);
                      }}
                    >
                      Quitar
                    </Button>
                  </div>
                ) : (
                  <small className="text-muted">Escribí al menos 2 caracteres para buscar.</small>
                )}

                {(clienteLoadingCreate || clienteErrorCreate || clienteResultadosCreate.length > 0) && (
                  <div className="list-group mt-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                    {clienteLoadingCreate && <div className="list-group-item">Buscando...</div>}

                    {!clienteLoadingCreate && clienteErrorCreate && (
                      <div className="list-group-item text-danger">{clienteErrorCreate}</div>
                    )}

                    {!clienteLoadingCreate &&
                      !clienteErrorCreate &&
                      clienteResultadosCreate.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="list-group-item list-group-item-action"
                          onClick={() => {
                            setNuevoComprobante((p) => ({ ...p, cliente_id: c.id }));
                            setClientesCache((prev) => ({ ...prev, [Number(c.id)]: c })); // cache
                            setClienteQueryCreate(clienteLabel(c));
                            setClienteResultadosCreate([]);
                          }}
                        >
                          {clienteLabel(c)}
                        </button>
                      ))}
                  </div>
                )}
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Neto</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="neto"
                  value={nuevoComprobante.neto}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-4">
                <Form.Label>IVA 10.5%</Form.Label>

                <div className="d-flex gap-2">
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="iva105"
                    value={nuevoComprobante.iva105}
                    onChange={handleNuevoChange}
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={calcIVA105}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Calc
                  </Button>
                </div>
              </Form.Group>



              <Form.Group className="mb-3 col-md-4">
                <Form.Label>IVA 21%</Form.Label>

                <div className="d-flex gap-2">
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="iva21"
                    value={nuevoComprobante.iva21}
                    onChange={handleNuevoChange}
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={calcIVA21}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Calc
                  </Button>
                </div>
              </Form.Group>


            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Total (auto)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="total"
                  value={toNumber2(derivedTotal).toFixed(2)}
                  readOnly
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Forma de Pago (solo informativo)</Form.Label>
                <Form.Select
                  name="formapago_id"
                  value={nuevoComprobante.formapago_id}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {formasPagoTesoreria.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Tipo Comprobante</Form.Label>
                <Form.Select
                  name="tipocomprobante_id"
                  value={nuevoComprobante.tipocomprobante_id}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {tiposComprobanteTabla.map((tc) => (
                    <option key={tc.id} value={tc.id}>
                      {tc.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Punto de Venta</Form.Label>
                <Form.Select
                  name="ptoventa_id"
                  value={nuevoComprobante.ptoventa_id}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {ptosVtaFiltrados.map((pv) => (
                    <option key={pv.id} value={pv.id}>
                      {pv.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Libro IVA</Form.Label>
                <Form.Select
                  name="libroiva_id"
                  value={nuevoComprobante.libroiva_id || ""}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {librosIVAFiltrados.map((l) => (
                    <option key={l.id} value={l.id}>
                      {`${l.mes} / ${l.anio}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Imputación Contable</Form.Label>
                <Form.Select
                  name="imputacioncontable_id"
                  value={nuevoComprobante.imputacioncontable_id}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {imputacionContableTabla.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Fecha Comprobante</Form.Label>
                <Form.Control
                  type="date"
                  name="fechacomprobante"
                  value={nuevoComprobante.fechacomprobante}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Fecha Vencimiento</Form.Label>
                <Form.Control
                  type="date"
                  name="fechavencimiento"
                  value={nuevoComprobante.fechavencimiento}
                  onChange={handleNuevoChange}
                />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                as="textarea"
                name="observaciones"
                rows={2}
                value={nuevoComprobante.observaciones}
                onChange={handleNuevoChange}
              />
            </Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Estado Pago</Form.Label>
                <Form.Select
                  name="estadoPago"
                  value={nuevoComprobante.estadoPago}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="parcial">Parcial</option>
                  <option value="pagado">Pagado</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3 col-md-6 d-flex align-items-end">
                <Form.Check
                  label="Con Factura"
                  name="conFactura"
                  checked={nuevoComprobante.conFactura}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              {(() => {
                const tipo = tiposComprobanteTabla.find(
                  (t) => Number(t.id) === Number(nuevoComprobante.tipocomprobante_id)
                );

                const descripcion = String(tipo?.descripcion || "")
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .toUpperCase();

                const esNotaCredito = /NOTA\s+(DE\s+)?CREDITO/.test(descripcion);

                if (!esNotaCredito) return null;

                return (
                  <Form.Group className="mb-3 col-md-6">
                    <Form.Check
                      type="switch"
                      label="Corrige un Cierre Z"
                      name="ajusta_cierre_z"
                      checked={!!nuevoComprobante.ajusta_cierre_z}
                      onChange={handleNuevoChange}
                    />
                    <Form.Text className="text-muted">
                      Descuenta el IVA de esta nota de crédito antes de proyectar los Cierres Z.
                    </Form.Text>
                  </Form.Group>
                );
              })()}
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCreateModal(false);
            setNuevoComprobante(getNuevoComprobanteInicial()); // ← reset
            setClienteQueryCreate("");
            setClienteResultadosCreate([]);
            setClienteErrorCreate("");
            setClienteLoadingCreate(false);
          }}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleCrearComprobante}
            disabled={!canCrearComprobante}
          >
            Crear Comprobante
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
